/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { ENVIRONMENT } from '../../../../../environment/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { finalize, map, Observable, tap } from 'rxjs';
import {
    CreateUserRequest,
    Device,
    DeviceModel,
    EmailVerificationRequest,
    LoginRequest,
    LoginResponse,
    ResendEmailResponse,
} from '../../models/authentication.model';
import { ApiResponse } from '../../../../core/models/api-response.model';
import { encryptString } from '../../../../core/helpers/string-utils';
import { StorageService } from '../../../../core/services/storage.service';
import { Router } from '@angular/router';
import {
    ChangePasswordInterface,
    StripePaymentInterface,
    UpdateUserTutorialInterface,
    UserDetailsInterface,
    UserPreference,
} from '../../../../core/models/user.model';
import { ErrorService } from '../../../../core/services/error.service';

@Injectable({
    providedIn: 'root',
})
export class UsersService {
    apiRoute = `${ENVIRONMENT.apiDomain}/api/v1/Users`;

    constructor(
        private errorService: ErrorService,
        private httpClient: HttpClient,
        private router: Router,
        private storageService: StorageService,
    ) {}

    public getUserDetails(): Observable<ApiResponse<UserDetailsInterface>> {
        const userDetailsApi = `${this.apiRoute}/${this.storageService.userId}`;

        return this.httpClient.get<ApiResponse<UserDetailsInterface>>(userDetailsApi);
    }

    public logout(): Observable<boolean> {
        return this.httpClient.post<boolean>(`${this.apiRoute}/logout`, null).pipe(
            tap({
                error: (error) => {
                    this.errorService.showError(error);
                },
            }),
            finalize(() => {
                this.storageService.clear();
                this.router.navigate(['/login']);
            }),
        );
    }

    public login(requestBody: LoginRequest, isRememberMe: boolean): Observable<LoginResponse> {
        const loginApi = `${this.apiRoute}/login`;
        const params = { noToken: 'noToken' };

        requestBody.password = encryptString(requestBody.password);

        return this.httpClient.post<ApiResponse<LoginResponse>>(loginApi, requestBody, { params: params }).pipe(
            map((response) => {
                return (response.result ? response.result : {}) as LoginResponse;
            }),
            tap((loginResponseResult: LoginResponse) => {
                this.storageService.setFromLogin(loginResponseResult, requestBody.device);
                this.storageService.setRememberMeValue(isRememberMe);
                this.storageService.clearClipVisitorId();
            }),
        );
    }

    public refreshToken(): Observable<LoginResponse> {
        const refreshTokenApi = `${this.apiRoute}/refreshToken`;
        const token = this.storageService.refreshToken;
        const device = new DeviceModel({} as Device, this.storageService);

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        });

        return this.httpClient
            .post<
                ApiResponse<LoginResponse>
            >(refreshTokenApi, null, { headers: headers, params: { noToken: '1', refresh: '1' } })
            .pipe(
                map((response) => {
                    return (response.result ? response.result : {}) as LoginResponse;
                }),
                tap({
                    next: (response) => {
                        this.storageService.setFromLogin(response, device);
                    },
                    error: () => {
                        this.storageService.clear();
                        this.router.navigate(['/home'], { onSameUrlNavigation: 'reload' });
                    },
                }),
            );
    }

    public createUser(requestBody: CreateUserRequest): Observable<StripePaymentInterface> {
        const params = { noToken: 'noToken' };

        return this.httpClient
            .post<ApiResponse<StripePaymentInterface>>(`${this.apiRoute}/create-user`, requestBody, { params: params })
            .pipe(
                map((response) => {
                    return (response.result ? response.result : {}) as StripePaymentInterface;
                }),
            );
    }

    public resetPassword(
        requestBody: { email: string; newPassword: string },
        accessToken: string,
    ): Observable<boolean> {
        const createPasswordApi = `${this.apiRoute}/reset-password`;
        const params = { noToken: 'noToken' };
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        });

        requestBody.newPassword = encryptString(requestBody.newPassword);

        return this.httpClient
            .post<ApiResponse<boolean>>(createPasswordApi, requestBody, { headers: headers, params: params })
            .pipe(
                map((response) => {
                    return response.result || false;
                }),
            );
    }

    public forgotPassword(email: string): Observable<boolean> {
        const forgotPasswordApi = `${this.apiRoute}/forget-password`;
        const params = { noToken: 'noToken' };

        return this.httpClient.post<ApiResponse<boolean>>(forgotPasswordApi, { email: email }, { params: params }).pipe(
            map((response) => {
                return response.result || false;
            }),
        );
    }

    public resendEmail(email: string): Observable<boolean> {
        const resendEmailApi = `${this.apiRoute}/resendEmail`;
        const params = { noToken: 'noToken' };

        return this.httpClient.post<ApiResponse<boolean>>(resendEmailApi, { email: email }, { params: params }).pipe(
            map((response) => {
                return response.result || false;
            }),
        );
    }

    // TPOD-77: New banner email verification functions
    public resendVerificationEmailBanner(email: string): Observable<ResendEmailResponse> {
        const resendEmailApi = `${this.apiRoute}/resendVerificationEmailBanner`;

        return this.httpClient.post<{ result: ResendEmailResponse }>(resendEmailApi, { email: email }).pipe(
            map((response) => {
                // Backend wraps response in { result: {...} }
                return (response.result || response) as ResendEmailResponse;
            }),
        );
    }

    public changePassword(body: ChangePasswordInterface): Observable<ApiResponse<boolean>> {
        body.oldPassword = encryptString(body.oldPassword);
        body.newPassword = encryptString(body.newPassword);

        return this.httpClient.post<ApiResponse<boolean>>(`${this.apiRoute}/changePassword`, body).pipe(
            map((response) => {
                return (response.result || {}) as ApiResponse<boolean>;
            }),
        );
    }

    public verifyResetPasswordToken(email: string, accessToken: string): Observable<ApiResponse<boolean>> {
        const params = { noToken: 'noToken' };
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        });

        return this.httpClient
            .post<
                ApiResponse<boolean>
            >(`${this.apiRoute}/verify-reset-password-token`, { email: email }, { headers: headers, params: params })
            .pipe(
                map((response) => {
                    return (response.result || {}) as ApiResponse<boolean>;
                }),
            );
    }

    public updateUserTutorial(data: UpdateUserTutorialInterface): Observable<ApiResponse<boolean>> {
        return this.httpClient.post<ApiResponse<boolean>>(`${this.apiRoute}/update-user-tutorial`, data);
    }

    public verifyEmail(body: EmailVerificationRequest, accessToken: string): Observable<ApiResponse<LoginResponse>> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${accessToken}`,
        });

        return this.httpClient.post<ApiResponse<LoginResponse>>(`${this.apiRoute}/verifyEmail`, body, { headers });
    }

    public verifyEmailLink(body: EmailVerificationRequest, accessToken: string): Observable<ApiResponse<boolean>> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${accessToken}`,
        });

        // noToken prevents ACCESS_TOKEN_INTERCEPTOR from overwriting our email-token header
        const params = new HttpParams().set('noToken', 'noToken');

        return this.httpClient.post<ApiResponse<boolean>>(`${this.apiRoute}/verifyEmailLink`, body, { headers, params });
    }

    // TPOD-77: New banner email verification function
    public verifyEmailBanner(body: EmailVerificationRequest, emailToken: string): Observable<{ result: { success: boolean; data: { accessToken: string; refreshToken: string; verifiedEmailDate: string } } }> {
        const verifyEmailApi = `${this.apiRoute}/verifyEmailBanner`;
        const headers = new HttpHeaders({
            Authorization: `Bearer ${emailToken}`,
        });

        // Add noToken parameter to prevent ACCESS_TOKEN_INTERCEPTOR from overwriting our Authorization header
        // The interceptor has logic to skip token injection when noToken query param is present
        const params = new HttpParams().set('noToken', 'noToken');

        return this.httpClient.post<{ result: { success: boolean; data: { accessToken: string; refreshToken: string; verifiedEmailDate: string } } }>(verifyEmailApi, body, { headers, params });
    }
    
    /**
     * Re-authenticates user after device revocation using temporary token
     * Used in device limit exceeded flow after user removes a device
     *
     * Following Single Responsibility: Handles only re-authentication after device removal
     * Following Dependency Inversion: Depends on HttpClient abstraction
     *
     * @param body - DeviceModel containing current device information
     * @param authorizeToken - Temporary access token provided during device limit error
     * @returns Observable of ApiResponse containing LoginResponse with new authentication tokens
     */
    public reauthAfterRevoke(body: DeviceModel, authorizeToken: string): Observable<ApiResponse<LoginResponse>> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${authorizeToken}`,
        });

        return this.httpClient.post<ApiResponse<LoginResponse>>(`${this.apiRoute}/reauth-after-revoke`, body, { headers });
    }

    public getPreference(): Observable<ApiResponse<UserPreference[]>> {
        return this.httpClient.get<ApiResponse<UserPreference[]>>(`${this.apiRoute}/UserPreference`);
    }

    public updatePreference(body: Partial<UserPreference>): Observable<ApiResponse<UserPreference[]>> {
        return this.httpClient.post<ApiResponse<UserPreference[]>>(`${this.apiRoute}/UserPreference`, body);
    }
}
